import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TalentCard from '../components/TalentCard';
import AutocompleteInput from '../components/AutocompleteInput';
import { userApi, autocompleteApi } from '../lib/api';
import { Search, SlidersHorizontal, X, Loader2, ArrowUpDown, Lock, ChevronDown } from 'lucide-react';

const Browse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [talents, setTalents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    minRate: '',
    maxRate: '',
    sortBy: searchParams.get('sort') || 'rating'
  });

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate('/login?redirect=/browse');
      return;
    }
    loadCategories();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadTalents();
    }
  }, [searchParams, isAuthenticated]);

  const loadCategories = async () => {
    try {
      const response = await userApi.getCategories({ sort: 'alphabetical' });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadTalents = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchParams.get('search') || undefined,
        category: searchParams.get('category') || undefined,
        location: searchParams.get('location') || undefined,
        min_rate: searchParams.get('minRate') || undefined,
        max_rate: searchParams.get('maxRate') || undefined,
        sort_by: searchParams.get('sort') || 'rating',
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await userApi.getTalents(params);
      setTalents(response.data);
    } catch (error) {
      console.error('Failed to load talents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.location) params.set('location', filters.location);
    if (filters.minRate) params.set('minRate', filters.minRate);
    if (filters.maxRate) params.set('maxRate', filters.maxRate);
    if (filters.sortBy) params.set('sort', filters.sortBy);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', location: '', minRate: '', maxRate: '' });
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Browse Talents
          </h1>
          <p className="text-muted-foreground">
            Discover and book amazing professionals for your next project
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-3">
            {/* Category Dropdown */}
            <Select
              value={filters.category || "all"}
              onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
            >
              <SelectTrigger className="h-12 w-48 rounded-xl" data-testid="browse-category-dropdown">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.sort((a, b) => a.localeCompare(b)).map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Search Input with Autocomplete */}
            <div className="flex-1">
              <AutocompleteInput
                value={filters.search}
                onChange={(value) => setFilters({ ...filters, search: value })}
                onSelect={(item) => {
                  if (item.type === 'category') {
                    setFilters({ ...filters, category: item.value, search: '' });
                  } else {
                    setFilters({ ...filters, search: item.value });
                  }
                  // Auto-submit on select
                  setTimeout(() => handleSearch({ preventDefault: () => {} }), 100);
                }}
                fetchSuggestions={(q) => autocompleteApi.talents(q)}
                placeholder="Search by name, skill, or keyword..."
                type="talent"
                className="flex-1"
              />
            </div>
            <Button type="submit" className="h-12 px-6 rounded-xl" data-testid="browse-search-btn">
              Search
            </Button>
          </form>

          <Button
            variant="outline"
            className="h-12 rounded-xl"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="browse-filters-btn"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 bg-primary text-primary-foreground">Active</Badge>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="glass-panel rounded-2xl p-6 mb-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select
                  value={filters.category || "all"}
                  onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Location</label>
                <Input
                  placeholder="City or region"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  className="h-12 rounded-xl"
                  data-testid="filter-location"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Min Starting Rate ($)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minRate}
                  onChange={(e) => setFilters({ ...filters, minRate: e.target.value })}
                  className="h-12 rounded-xl"
                  data-testid="filter-min-rate"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Max Starting Rate ($)</label>
                <Input
                  type="number"
                  placeholder="Any"
                  value={filters.maxRate}
                  onChange={(e) => setFilters({ ...filters, maxRate: e.target.value })}
                  className="h-12 rounded-xl"
                  data-testid="filter-max-rate"
                />
              </div>
            </div>

            {/* Sort Options */}
            <div className="pt-4 border-t border-border">
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4" />
                Sort By
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'rating', label: 'Top Rated' },
                  { value: 'price_low', label: 'Rate: Low to High' },
                  { value: 'price_high', label: 'Rate: High to Low' },
                  { value: 'name', label: 'Name (A-Z)' },
                  { value: 'name_desc', label: 'Name (Z-A)' },
                  { value: 'recent', label: 'Newest' },
                  { value: 'reviews', label: 'Most Reviews' },
                ].map(option => (
                  <Button
                    key={option.value}
                    variant={filters.sortBy === option.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilters({ ...filters, sortBy: option.value })}
                    className="rounded-full text-xs"
                    data-testid={`sort-${option.value}`}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={applyFilters} className="rounded-xl" data-testid="apply-filters-btn">
                Apply Filters
              </Button>
              <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </div>
        )}

        {/* Active Filters Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.search && (
              <Badge variant="secondary" className="rounded-full px-3 py-1">
                Search: {filters.search}
                <button onClick={() => { setFilters({ ...filters, search: '' }); applyFilters(); }} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.category && (
              <Badge variant="secondary" className="rounded-full px-3 py-1">
                {filters.category}
                <button onClick={() => { setFilters({ ...filters, category: '' }); applyFilters(); }} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : talents.length > 0 ? (
          <div className="grid grid-cols-12 gap-6">
            {talents.map(talent => (
              <TalentCard key={talent.user_id} talent={talent} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg mb-4">No talents found matching your criteria</p>
            <Button onClick={clearFilters} variant="outline" className="rounded-full">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Browse;
